<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue-sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth.store';
import BaseButton from '@components/atoms/BaseButton.vue';
import PasswordInput from '@components/atoms/PasswordInput.vue';
import FormField from '@components/molecules/FormField.vue';
import { useChangePasswordForm } from '@composables/useChangePasswordForm';

interface Props {
  open: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:open': [open: boolean];
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const { form: passwordForm, reset, toRequest, isValid } = useChangePasswordForm();

const submitting = ref(false);

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      reset();
    }
  },
);

function fieldError(meta: { isTouched: boolean; errors: unknown[] }, fieldLabel: string): string {
  if (!meta.isTouched || !meta.errors[0]) {
    return '';
  }

  return t(String(meta.errors[0]), { field: fieldLabel });
}

async function handleSubmit(): Promise<void> {
  if (!isValid.value || submitting.value) {
    return;
  }

  submitting.value = true;

  try {
    await authStore.changePassword(toRequest());

    toast.success(t('auth.changePassword.success'));
    emit('update:open', false);
  } catch (error) {
    const message = error instanceof Error ? error.message : '';

    toast.error(message || t('auth.changePassword.error'));
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('auth.changePassword.title') }}</DialogTitle>
        <DialogDescription>{{ t('auth.changePassword.description') }}</DialogDescription>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="handleSubmit">
        <passwordForm.Field v-slot="{ field }" name="currentPassword">
          <FormField
            :label="t('auth.changePassword.currentPassword')"
            :error="fieldError(field.state.meta, t('auth.changePassword.currentPassword'))"
            required
          >
            <PasswordInput
              :model-value="field.state.value"
              :error="fieldError(field.state.meta, t('auth.changePassword.currentPassword'))"
              autocomplete="current-password"
              @update:model-value="field.handleChange"
              @focusout="field.handleBlur"
            />
          </FormField>
        </passwordForm.Field>

        <passwordForm.Field v-slot="{ field }" name="newPassword">
          <FormField
            :label="t('auth.changePassword.newPassword')"
            :error="fieldError(field.state.meta, t('auth.changePassword.newPassword'))"
            required
          >
            <PasswordInput
              :model-value="field.state.value"
              :error="fieldError(field.state.meta, t('auth.changePassword.newPassword'))"
              autocomplete="new-password"
              @update:model-value="field.handleChange"
              @focusout="field.handleBlur"
            />
          </FormField>
        </passwordForm.Field>

        <passwordForm.Field v-slot="{ field }" name="confirmPassword">
          <FormField
            :label="t('auth.changePassword.confirmPassword')"
            :error="fieldError(field.state.meta, t('auth.changePassword.confirmPassword'))"
            required
          >
            <PasswordInput
              :model-value="field.state.value"
              :error="fieldError(field.state.meta, t('auth.changePassword.confirmPassword'))"
              autocomplete="new-password"
              @update:model-value="field.handleChange"
              @focusout="field.handleBlur"
            />
          </FormField>
        </passwordForm.Field>

        <DialogFooter>
          <BaseButton type="button" variant="outline" @click="emit('update:open', false)">
            {{ t('common.actions.cancel') }}
          </BaseButton>
          <BaseButton type="submit" :disabled="!isValid || submitting">
            <Loader2 v-if="submitting" class="animate-spin" />
            {{ t('auth.changePassword.submit') }}
          </BaseButton>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
